/**
 * Load optional component definitions when their elements are present.
 * Observe added subtrees so section rendering, recommendations and the theme
 * editor work even when these components weren't on the initial page.
 */
const componentModules = new Map([
  ['product-sku-component', () => import('@theme/product-sku')],
  ['product-inventory', () => import('@theme/product-inventory')],
  ['volume-pricing', () => import('@theme/volume-pricing')],
  ['price-per-item', () => import('@theme/price-per-item')],
  ['volume-pricing-info', () => import('@theme/volume-pricing-info')],
  ['layered-slideshow-component', () => import('@theme/layered-slideshow')],
  ['show-more-component', () => import('@theme/show-more')],
  ['video-background-component', () => import('@theme/video-background')],
]);

const selector = [...componentModules.keys()].join(',');
/** @type {Map<string, Promise<unknown>>} */
const pendingModules = new Map();

/**
 * @param {string} name
 * @returns {Promise<unknown> | undefined}
 */
function loadComponent(name) {
  if (customElements.get(name)) return;
  const load = componentModules.get(name);
  if (!load) return;

  if (!pendingModules.has(name)) {
    pendingModules.set(
      name,
      load().catch((error) => {
        // Keep the server-rendered content usable if an optional module fails.
        console.warn(`[component-loader] Could not load ${name}:`, error);
      })
    );
  }
  return pendingModules.get(name);
}

/**
 * Await this before inserting content that immediately dispatches component
 * events, such as a quick-add modal syncing its selected variant.
 * @param {ParentNode} root
 * @returns {Promise<unknown[]>}
 */
export function loadComponentModules(root) {
  const names = new Set();
  if (root instanceof Element && componentModules.has(root.localName)) names.add(root.localName);
  for (const element of root.querySelectorAll(selector)) names.add(element.localName);
  return Promise.all([...names].map(loadComponent));
}

const observer = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (node instanceof Element) loadComponentModules(node);
    }
  }
});
observer.observe(document.documentElement, { childList: true, subtree: true });
loadComponentModules(document);
